// src/components/cards/TodaysActiveBookingsCard.tsx
import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { BookingListItem, bookingsAPI } from '../../services/api';
import BookingsList from '../lists/BookingsList/BookingsList';
import LoadingSpinner from '../common/LoadingSpinner';
import DateRangePicker from '../forms/shared/DateRangePicker';

interface TodaysActiveBookingsCardProps {
  date: string;
  onDateChange?: (date: string) => void;
}

const CardContainer = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#ffffff',
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  padding: '1.5rem 2rem',
  borderBottom: `1px solid ${theme.palette.divider}`,
  gap: '2rem',
  flexWrap: 'wrap',
}));

const CountText = styled(Typography)(({ theme }) => ({
  fontSize: '1.8rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  whiteSpace: 'nowrap',
  textAlign: 'left',
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '3rem 2rem',
  color: theme.palette.text.secondary,
}));

const normalizeDate = (dateValue: string | Date): string => {
  if (!dateValue) return '';

  if (typeof dateValue === 'string') {
    return dateValue.split('T')[0];
  }

  const d = new Date(dateValue);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const TodaysActiveBookingsCard: React.FC<TodaysActiveBookingsCardProps> = ({
  date,
  onDateChange,
}) => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<BookingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!date) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    bookingsAPI
      .getAll({})
      .then((allBookings) => {
        const normalizedTarget = normalizeDate(date);

        const activeBookings = allBookings.filter((booking) => {
          const normalizedCheckIn = normalizeDate(booking.checkInDate);
          const normalizedCheckOut = normalizeDate(booking.checkOutDate);
          return (
            normalizedCheckIn <= normalizedTarget &&
            normalizedCheckOut >= normalizedTarget
          );
        });

        const sorted = [...activeBookings].sort((a, b) => b.id - a.id);
        setBookings(sorted);
      })
      .catch(() => {
        setError('Failed to load bookings');
        setBookings([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [date]);

  const displayCount = bookings.length;

  if (loading) {
    return (
      <CardContainer>
        <LoadingSpinner message="Loading..." size={40} />
      </CardContainer>
    );
  }

  if (error) {
    return (
      <CardContainer>
        <EmptyState>
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </EmptyState>
      </CardContainer>
    );
  }

  return (
    <CardContainer>
      <HeaderRow>
        <Box sx={{ flex: '0 0 auto' }}>
          <DateRangePicker
            mode="single"
            selectedDate={date}
            onDateChange={onDateChange || (() => {})}
          />
        </Box>
        <CountText>
          {displayCount} Active Booking{displayCount !== 1 ? 's' : ''}
        </CountText>
      </HeaderRow>

      {displayCount === 0 ? (
        <EmptyState>
          <Typography variant="body1">
            No active bookings on this date
          </Typography>
        </EmptyState>
      ) : (
        <BookingsList
          type="bookings"
          items={bookings}
          onEdit={(id) => navigate(`/booking/${id}/edit`)}
          onView={(id) => navigate(`/booking/${id}`)}
          onDelete={() => {}}
          onCancel={() => {}}
        />
      )}
    </CardContainer>
  );
};

export default TodaysActiveBookingsCard;
